import * as React from 'react';
import * as styles from './styles.scss';
import { observer } from 'mobx-react-lite';
import { ControlIcons } from 'owa-control-icons';
import { Icon } from '@fluentui/react/lib/Icon';
import {
    unreadRepliesLabel,
    twoUnreadRepliesExpandedAriaLabel,
    twoUnreadRepliesCollapsedAriaLabel,
    threeOrMoreUnreadRepliesExpandedAriaLabel,
    threeOrMoreUnreadRepliesCollapsedAriaLabel,
    singleUnreadReplyLabel,
    singularUnreadReplyExpandedAriaLabel,
    singularUnreadReplyCollapsedAriaLabel,
} from './UnreadReplies.locstring.json';
import loc from 'owa-localize';
import { KeyCodes } from '@fluentui/react/lib/Utilities';
import { logSpacesDatapoint } from 'owa-timestream-logging';

export default observer(function UnreadReplies({
    unreadRepliesCount,
    unreadReplies,
    assetType,
}: {
    unreadRepliesCount: number;
    unreadReplies: React.ReactElement;
    assetType: string;
}) {
    const [areRepliesVisible, setVisibleState] = React.useState<boolean>(false);
    const onKeyDown = React.useCallback(
        (ev: React.KeyboardEvent) => {
            if (ev.keyCode == KeyCodes.enter) {
                setVisibleState(!areRepliesVisible);
            }
        },
        [areRepliesVisible]
    );

    const onClick = React.useCallback(
        (ev: React.MouseEvent) => {
            const newState = !areRepliesVisible;
            logSpacesDatapoint('OUTLOOKSPACES_UNREAD_REPLIES_TOGGLED', {
                isExpanded: newState.toString(),
                assetType: assetType,
            });
            setVisibleState(!areRepliesVisible);
        },
        [areRepliesVisible]
    );

    const countString = unreadRepliesCount >= 3 ? '3+' : unreadRepliesCount.toString();
    let areRepliesVisibleAriaLabel;
    let unreadReplyLabel = loc(unreadRepliesLabel);

    switch (unreadRepliesCount) {
        case 1: {
            unreadReplyLabel = loc(singleUnreadReplyLabel);
            areRepliesVisibleAriaLabel = areRepliesVisible
                ? loc(singularUnreadReplyExpandedAriaLabel)
                : loc(singularUnreadReplyCollapsedAriaLabel);
            break;
        }
        case 2: {
            areRepliesVisibleAriaLabel = areRepliesVisible
                ? loc(twoUnreadRepliesExpandedAriaLabel)
                : loc(twoUnreadRepliesCollapsedAriaLabel);
            break;
        }
        default: {
            areRepliesVisibleAriaLabel = areRepliesVisible
                ? loc(threeOrMoreUnreadRepliesExpandedAriaLabel)
                : loc(threeOrMoreUnreadRepliesCollapsedAriaLabel);
            break;
        }
    }

    return (
        <div>
            <div
                aria-label={areRepliesVisibleAriaLabel}
                className={styles.unreadButton}
                onClick={onClick}
                onKeyDown={onKeyDown}
                role="button"
                tabIndex={0}>
                <div className={styles.unreadBadge}>
                    <div className={styles.unreadCount}>{countString}</div>
                </div>
                {unreadReplyLabel}
                <Icon
                    className={styles.unreadIcon}
                    iconName={
                        areRepliesVisible
                            ? ControlIcons.ChevronDownMed
                            : ControlIcons.ChevronRightMed
                    }
                />
            </div>
            {areRepliesVisible && <div className={styles.replyContainer}>{unreadReplies}</div>}
        </div>
    );
});
